import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import api from "../../services/api";
import AdminPageHeader, { AdminButton } from "../../components/admin/AdminPageHeader";

interface EmailTemplate {
  id: string;
  code: string;
  name: string;
  subject: string;
  body_html: string;
  description: string | null;
  variables: string[] | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export default function EmailTemplates() {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [resetting, setResetting] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);

  // Edit form state
  const [editSubject, setEditSubject] = useState("");
  const [editBodyHtml, setEditBodyHtml] = useState("");
  const [editIsActive, setEditIsActive] = useState(true);
  const [editorMode, setEditorMode] = useState<"html" | "wysiwyg">("html");

  // Test email state
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string; needsConfig?: boolean } | null>(null);

  // WYSIWYG ref
  const wysiwygRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  // Sync WYSIWYG content when switching modes
  useEffect(() => {
    if (editorMode === "wysiwyg" && wysiwygRef.current) {
      const doc = wysiwygRef.current.contentDocument;
      if (doc) {
        doc.open();
        doc.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <style>
              body { font-family: Arial, sans-serif; padding: 10px; margin: 0; }
              * { box-sizing: border-box; }
            </style>
          </head>
          <body contenteditable="true">${editBodyHtml}</body>
          </html>
        `);
        doc.close();

        // Listen for changes
        doc.body.addEventListener("input", () => {
          setEditBodyHtml(doc.body.innerHTML);
        });
      }
    }
  }, [editorMode]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/email-templates");
      setTemplates(data);
    } catch (error) {
      console.error("Failed to load email templates:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedTemplates = async () => {
    try {
      setSeeding(true);
      await api.post("/email-templates/seed");
      await loadTemplates();
    } catch (error) {
      console.error("Failed to seed templates:", error);
    } finally {
      setSeeding(false);
    }
  };

  const handleEdit = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setEditSubject(template.subject);
    setEditBodyHtml(template.body_html);
    setEditIsActive(template.is_active);
    setEditorMode("html");
    setShowEditModal(true);
  };

  const handleSave = async () => {
    if (!selectedTemplate) return;

    try {
      setSaving(true);
      await api.put(`/email-templates/${selectedTemplate.code}`, {
        subject: editSubject,
        body_html: editBodyHtml,
        is_active: editIsActive,
      });
      await loadTemplates();
      setShowEditModal(false);
      setSelectedTemplate(null);
    } catch (error) {
      console.error("Failed to save template:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (code: string) => {
    if (!window.confirm("Ești sigur că vrei să resetezi acest șablon la valorile implicite?")) {
      return;
    }

    try {
      setResetting(code);
      await api.post(`/email-templates/${code}/reset`);
      await loadTemplates();
    } catch (error) {
      console.error("Failed to reset template:", error);
    } finally {
      setResetting(null);
    }
  };

  const handlePreview = async (template: EmailTemplate, customContent?: { subject: string; body_html: string }) => {
    try {
      setPreviewing(true);
      const payload = customContent
        ? { subject: customContent.subject, body_html: customContent.body_html }
        : {};

      const { data } = await api.post(`/email-templates/${template.code}/preview`, payload);
      setPreviewSubject(data.subject);
      setPreviewHtml(data.body_html);
      setShowPreviewModal(true);
    } catch (error) {
      console.error("Failed to preview template:", error);
      alert("Eroare la previzualizare. Verificați consola pentru detalii.");
    } finally {
      setPreviewing(false);
    }
  };

  const handlePreviewFromEdit = () => {
    if (selectedTemplate) {
      handlePreview(selectedTemplate, { subject: editSubject, body_html: editBodyHtml });
    }
  };

  const handleOpenTestModal = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setTestEmail("");
    setTestResult(null);
    setShowTestModal(true);
  };

  const handleSendTestEmail = async () => {
    if (!selectedTemplate || !testEmail) return;

    try {
      setSendingTest(true);
      setTestResult(null);

      const { data } = await api.post(`/email-templates/${selectedTemplate.code}/test`, {
        email: testEmail,
      });

      setTestResult({ success: true, message: data.message });
    } catch (error: any) {
      const needsConfig = error.response?.data?.needsConfig;
      const message = error.response?.data?.error || "Eroare la trimiterea emailului de test";
      setTestResult({ success: false, message, needsConfig });
    } finally {
      setSendingTest(false);
    }
  };

  const getTemplateIcon = (code: string) => {
    if (code.includes("welcome")) {
      return (
        <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
          <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      );
    }
    if (code.includes("invitation")) {
      return (
        <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
          <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
      );
    }
    if (code.includes("invoice")) {
      return (
        <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
      );
    }
    return (
      <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
        <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6 mx-auto max-w-[1500px]">
      {/* Header */}
      <AdminPageHeader
        eyebrow="Comunicare"
        title="Șabloane Email"
        subtitle="Gestionează conținutul emailurilor trimise de platformă"
        icon={
          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        }
        actions={
          templates.length === 0 && !loading ? (
            <AdminButton onClick={handleSeedTemplates} disabled={seeding}>
              {seeding ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Se creează...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Creează șabloane implicite
                </>
              )}
            </AdminButton>
          ) : undefined
        }
      />

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <svg className="w-6 h-6 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Variabile disponibile în șabloane:</p>
            <div className="flex flex-wrap gap-2">
              {["user_name", "user_email", "coach_name", "club_name", "club_address", "team_name", "date", "login_url", "accept_url"].map((v) => (
                <code key={v} className="px-2 py-0.5 bg-blue-100 rounded text-xs font-mono">
                  {`{{${v}}}`}
                </code>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Templates List */}
      {loading ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full mx-auto" />
          <p className="mt-4 text-slate-500">Se încarcă...</p>
        </div>
      ) : templates.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <svg className="w-16 h-16 text-slate-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <p className="text-slate-500 mb-4">Nu există șabloane de email</p>
          <p className="text-sm text-slate-400">Apasă butonul de mai sus pentru a crea șabloanele implicite.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {templates.map((template) => (
            <div
              key={template.id}
              className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start gap-4">
                {getTemplateIcon(template.code)}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-slate-800">{template.name}</h3>
                    {!template.is_active && (
                      <span className="px-2 py-0.5 text-xs font-medium text-amber-700 bg-amber-100 rounded-full">
                        Inactiv
                      </span>
                    )}
                    <code className="px-2 py-0.5 text-xs text-slate-500 bg-slate-100 rounded">
                      {template.code}
                    </code>
                  </div>
                  {template.description && (
                    <p className="text-sm text-slate-500 mb-2">{template.description}</p>
                  )}
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <span className="font-medium">Subiect:</span>
                    <span className="truncate">{template.subject}</span>
                  </div>
                  {template.variables && template.variables.length > 0 && (
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs text-slate-400">Variabile:</span>
                      <div className="flex flex-wrap gap-1">
                        {template.variables.map((v) => (
                          <code key={v} className="px-1.5 py-0.5 text-xs text-slate-500 bg-slate-50 rounded">
                            {`{{${v}}}`}
                          </code>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => handlePreview(template)}
                    disabled={previewing}
                    className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors flex items-center gap-1 disabled:opacity-50"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    Previzualizare
                  </button>
                  <button
                    onClick={() => handleOpenTestModal(template)}
                    className="px-3 py-1.5 text-sm text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                    Test
                  </button>
                  <button
                    onClick={() => handleEdit(template)}
                    className="px-3 py-1.5 text-sm text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Editează
                  </button>
                  <button
                    onClick={() => handleReset(template.code)}
                    disabled={resetting === template.code}
                    className="px-3 py-1.5 text-sm text-amber-600 hover:bg-amber-50 rounded-lg transition-colors flex items-center gap-1 disabled:opacity-50"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {resetting === template.code ? "..." : "Resetează"}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
              <h2 className="text-lg font-semibold text-slate-800">
                Editează: {selectedTemplate.name}
              </h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedTemplate(null);
                }}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <div className="space-y-4">
                {/* Active toggle */}
                <div className="flex items-center gap-3">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editIsActive}
                      onChange={(e) => setEditIsActive(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                  <span className="text-sm text-slate-600">Șablon activ</span>
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">
                    Subiect email
                  </label>
                  <input
                    type="text"
                    value={editSubject}
                    onChange={(e) => setEditSubject(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Subiectul emailului..."
                  />
                  <p className="mt-1 text-xs text-slate-400">
                    Poți folosi variabile precum {`{{user_name}}`} în subiect.
                  </p>
                </div>

                {/* Variables info */}
                {selectedTemplate.variables && selectedTemplate.variables.length > 0 && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm font-medium text-slate-600 mb-2">Variabile disponibile:</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedTemplate.variables.map((v) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(`{{${v}}}`);
                          }}
                          className="px-2 py-1 text-xs font-mono text-indigo-600 bg-indigo-50 rounded hover:bg-indigo-100 transition-colors"
                          title="Apasă pentru a copia"
                        >
                          {`{{${v}}}`}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Editor Mode Toggle */}
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-slate-600">
                    Conținut email
                  </label>
                  <div className="flex items-center bg-slate-100 rounded-lg p-1">
                    <button
                      onClick={() => setEditorMode("html")}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        editorMode === "html"
                          ? "bg-white text-slate-800 shadow-sm"
                          : "text-slate-600 hover:text-slate-800"
                      }`}
                    >
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                        </svg>
                        HTML
                      </span>
                    </button>
                    <button
                      onClick={() => setEditorMode("wysiwyg")}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        editorMode === "wysiwyg"
                          ? "bg-white text-slate-800 shadow-sm"
                          : "text-slate-600 hover:text-slate-800"
                      }`}
                    >
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        Vizual
                      </span>
                    </button>
                  </div>
                </div>

                {/* HTML Editor */}
                {editorMode === "html" && (
                  <div>
                    <textarea
                      value={editBodyHtml}
                      onChange={(e) => setEditBodyHtml(e.target.value)}
                      rows={20}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                      placeholder="Codul HTML al emailului..."
                    />
                    <p className="mt-1 text-xs text-slate-400">
                      Important: Folosește CSS inline pentru compatibilitate cu clienții de email.
                    </p>
                  </div>
                )}

                {/* WYSIWYG Editor */}
                {editorMode === "wysiwyg" && (
                  <div>
                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                      <iframe
                        ref={wysiwygRef}
                        title="Editor WYSIWYG"
                        className="w-full bg-white"
                        style={{ height: "450px" }}
                      />
                    </div>
                    <p className="mt-1 text-xs text-slate-400">
                      Atenție: Editarea vizuală poate modifica structura HTML. Verifică în modul HTML după editare.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between flex-shrink-0">
              <button
                onClick={handlePreviewFromEdit}
                disabled={previewing}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                Previzualizare
              </button>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedTemplate(null);
                  }}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  Anulează
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Se salvează...
                    </>
                  ) : (
                    "Salvează modificările"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-lg font-semibold text-slate-800">Previzualizare Email</h2>
                <p className="text-sm text-slate-500 mt-0.5">Subiect: {previewSubject}</p>
              </div>
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  setPreviewHtml("");
                  setPreviewSubject("");
                }}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-hidden">
              <iframe
                srcDoc={previewHtml}
                title="Previzualizare email"
                className="w-full h-full bg-white"
                style={{ minHeight: "500px" }}
                sandbox=""
              />
            </div>
          </div>
        </div>
      )}

      {/* Test Email Modal */}
      {showTestModal && selectedTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-800">
                Trimite Email Test
              </h2>
              <button
                onClick={() => {
                  setShowTestModal(false);
                  setSelectedTemplate(null);
                  setTestResult(null);
                }}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6">
              <p className="text-sm text-slate-600 mb-4">
                Trimite șablonul <strong>{selectedTemplate.name}</strong> la o adresă de email pentru a testa cum arată.
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-600 mb-1">
                  Adresa email de test
                </label>
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="email@exemplu.com"
                />
              </div>

              {testResult && (
                <div
                  className={`p-3 rounded-lg mb-4 ${
                    testResult.success
                      ? "bg-emerald-50 border border-emerald-200 text-emerald-700"
                      : "bg-red-50 border border-red-200 text-red-700"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {testResult.success ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    <span className="text-sm">{testResult.message}</span>
                  </div>
                  {testResult.needsConfig && (
                    <Link
                      to="/admin/settings"
                      className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-800"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Mergi la Setări
                    </Link>
                  )}
                </div>
              )}

              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => {
                    setShowTestModal(false);
                    setSelectedTemplate(null);
                    setTestResult(null);
                  }}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  Anulează
                </button>
                <button
                  onClick={handleSendTestEmail}
                  disabled={sendingTest || !testEmail}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {sendingTest ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Se trimite...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      Trimite Test
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
